import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessSection = () => {
  const processSteps = [
    {
      id: 1,
      title: "Initial Case Assessment",
      description: "Comprehensive review of case materials, identification of valuation issues, and preliminary opinion on case merits.",
      duration: "1-2 weeks",
      deliverables: ["Case assessment memo", "Preliminary timeline", "Fee estimate"],
      icon: "FileSearch"
    },
    {
      id: 2,
      title: "Expert Report Preparation",
      description: "Detailed valuation analysis with supporting documentation, methodology explanation, and defensible conclusions.",
      duration: "3-6 weeks",
      deliverables: ["Expert report", "Supporting schedules", "Data room access"],
      icon: "FileText"
    },
    {
      id: 3,
      title: "Deposition Participation",
      description: "Pre-deposition preparation, testimony under oath, and clarification of expert opinions and methodology.",
      duration: "1-2 days",
      deliverables: ["Deposition preparation", "Sworn testimony", "Follow-up clarifications"],
      icon: "MessageSquare"
    },
    {
      id: 4,
      title: "Courtroom Testimony",
      description: "Direct examination presentation, cross-examination defense, and expert opinion communication to judge/jury.",
      duration: "1-3 days",
      deliverables: ["Court testimony", "Visual aids", "Post-trial support"],
      icon: "Scale"
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-sky-100 text-sky-700 rounded-full px-4 py-2 text-sm font-medium mb-6">
            <Icon name="GitBranch" size={16} className="mr-2" />
            Litigation Support Process
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Structured Expert Witness Engagement
          </h2>

          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our systematic approach ensures thorough preparation, credible testimony, and successful case outcomes through every phase of litigation support.
          </p>
        </div>

        <div className="relative">
          {/* Process Timeline */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-sky-200 via-sky-400 to-sky-200"></div>

          <div className="space-y-12 lg:space-y-16">
            {processSteps?.map((step, index) => (
              <div key={step?.id} className={`relative flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}>
                {/* Timeline Node */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-sky-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center z-10">
                  <Icon name={step?.icon} size={20} className="text-white" />
                </div>

                {/* Content Card */}
                <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:pr-16' : 'lg:pl-16'
                  }`}>
                  <div className="bg-card border border-border rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="flex-shrink-0 w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center lg:hidden">
                        <Icon name={step?.icon} size={20} className="text-sky-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-500 text-white text-sm font-bold rounded-full">
                            {step?.id}
                          </span>
                          <span className="text-sm font-medium text-sky-600 bg-sky-50 px-3 py-1 rounded-full">
                            {step?.duration}
                          </span>
                        </div>
                        <h3 className="text-xl font-semibold text-primary mb-3">
                          {step?.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-text-secondary mb-6 leading-relaxed">
                      {step?.description}
                    </p>

                    <div>
                      <h4 className="text-sm font-semibold text-primary mb-3 uppercase tracking-wide">
                        Key Deliverables
                      </h4>
                      <div className="space-y-2">
                        {step?.deliverables?.map((deliverable, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <Icon name="CheckCircle" size={16} className="text-emerald-500 flex-shrink-0" />
                            <span className="text-sm text-text-secondary">{deliverable}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Spacer for opposite side */}
                <div className="hidden lg:block w-5/12"></div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-sky-50 to-slate-50 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl font-bold text-primary mb-4">
              Ready to Engage Expert Testimony?
            </h3>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Contact us for a confidential case assessment and timeline discussion. We provide preliminary opinions within 48 hours of initial consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2">
                <Icon name="Phone" size={18} />
                <span>Free Consultation</span>
              </button>
              <button className="border border-slate-300 hover:bg-slate-50 text-slate-700 px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2">
                <Icon name="Mail" size={18} />
                <span>Send Case Details</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;